import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../contractorApi';

// ── ステータス定義 ────────────────────────────────────────────────
const STATUS_META = {
    chatting:            { label: 'すり合わせ中',   bg: '#e3f2fd', color: '#1565c0' },
    estimated:           { label: '見積提出済み',   bg: '#fff3e0', color: '#f57c00' },
    decided:             { label: '受注確定',       bg: '#e8f5e9', color: '#2e7d32' },
    invoiced:            { label: '請求書提出済み', bg: '#fff3e0', color: '#f57c00' },
    paid:                { label: '入金確認',       bg: '#e8f5e9', color: '#2e7d32' },
    scheduled:           { label: '日程確定',       bg: '#e8eaf6', color: '#283593' },
    in_progress:         { label: '工事中',         bg: '#e0f2f1', color: '#00695c' },
    completion_notified: { label: '完了通知済み',   bg: '#e8f5e9', color: '#388e3c' },
    completed:           { label: '完了',           bg: '#e0e4ea', color: '#6b7280' },
    lost:                { label: '失注',           bg: '#f5f5f5', color: '#9ca3af' },
};

// 要対応ステータス
const ACTION_NEEDED = new Set(['chatting', 'decided', 'paid', 'in_progress']);

// ── seen管理 ──────────────────────────────────────────────────────
const SEEN_KEY = 'contractor_seen_projects';
const getSeenIds = () => JSON.parse(localStorage.getItem(SEEN_KEY) || '[]');
const markSeen = (projectId) => {
    const seen = getSeenIds();
    if (!seen.includes(projectId)) {
        localStorage.setItem(SEEN_KEY, JSON.stringify([...seen, projectId]));
    }
};

// ── アイコン ──────────────────────────────────────────────────────
const ArrowRight = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
    </svg>
);

const MessageIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
);

const EmptyIcon = () => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#005c53" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
        <rect x="9" y="3" width="6" height="4" rx="1" ry="1" />
        <line x1="9" y1="12" x2="15" y2="12" /><line x1="9" y1="16" x2="13" y2="16" />
    </svg>
);

// ── メイン ────────────────────────────────────────────────────────
const ContractorDashboard = () => {
    const contractorId = localStorage.getItem('contractor_id');
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [companyName, setCompanyName] = useState('');
    const [seenIds, setSeenIds] = useState(getSeenIds);
    const [loading, setLoading] = useState(true);

    const fetchProjects = () => {
        api.get(`projects/by_contractor/${contractorId}`)
            .then(r => setProjects(r.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        api.get(`contractors/${contractorId}`)
            .then(r => setCompanyName(r.data.company_name))
            .catch(console.error);
        fetchProjects();
        const timer = setInterval(fetchProjects, 10000);
        return () => clearInterval(timer);
    }, [contractorId]);

    const handleDetail = (projectId) => {
        markSeen(projectId);
        setSeenIds(getSeenIds());
        navigate(`/contractor/project/${projectId}`);
    };

    const activeProjects = projects.filter(p => !['lost', 'completed'].includes(p.status));
    const doneProjects   = projects.filter(p => ['lost', 'completed'].includes(p.status));
    const actionCount    = activeProjects.filter(p => ACTION_NEEDED.has(p.status)).length;
    const newCount       = activeProjects.filter(p => p.status === 'chatting' && !seenIds.includes(p.id)).length;

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
            <div style={{ width: 32, height: 32, border: '3px solid #e0e4ea', borderTopColor: '#005c53', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );

    return (
        <div style={{ minHeight: '100vh', background: '#f5f6f8' }}>

            {/* ページヘッダー */}
            <div style={{ background: '#141c3d', padding: '32px 0' }}>
                <div style={{ maxWidth: 960, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                    <div>
                        <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)', margin: '0 0 4px', letterSpacing: 1 }}>ダッシュボード</p>
                        <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                            {companyName || '読み込み中...'}
                        </h1>
                    </div>
                    <button
                        onClick={() => navigate('/contractor/progress')}
                        style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 4, fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer' }}
                    >
                        やることリスト
                    </button>
                </div>
            </div>

            <div style={{ maxWidth: 960, margin: '0 auto', padding: '28px 24px' }}>

                {/* サマリーカード */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 12, marginBottom: 32 }}>
                    {[
                        { label: '進行中', value: activeProjects.length },
                        { label: '要対応', value: actionCount,  accent: actionCount > 0 },
                        { label: '新着',   value: newCount,     accent: newCount > 0 },
                    ].map(({ label, value, accent }) => (
                        <div key={label} style={{
                            background: '#fff', borderRadius: 8, padding: '16px 8px',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.08)', textAlign: 'center',
                            border: accent ? '1px solid #fbbf24' : '1px solid transparent',
                        }}>
                            <div style={{ fontSize: '2rem', fontWeight: 700, color: accent ? '#f59e0b' : '#005c53', lineHeight: 1 }}>{value}</div>
                            <div style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: 6 }}>{label}</div>
                        </div>
                    ))}
                </div>

                {/* 進行中の案件 */}
                <div style={{ marginBottom: 32 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                        <h2 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>進行中の案件</h2>
                        <span style={{
                            minWidth: 26, height: 26, padding: '0 8px',
                            background: '#005c53', color: '#fff',
                            borderRadius: 13, fontSize: '0.78rem', fontWeight: 600,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            {activeProjects.length}
                        </span>
                    </div>

                    {activeProjects.length === 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '56px 0' }}>
                            <div style={{ width: 80, height: 80, background: 'rgba(0,92,83,0.08)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                                <EmptyIcon />
                            </div>
                            <p style={{ fontSize: '0.95rem', color: '#6b7280', margin: 0 }}>現在進行中の案件はありません</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {activeProjects.map(project => {
                                const meta = STATUS_META[project.status] || { label: project.status, bg: '#e0e4ea', color: '#6b7280' };
                                const isNew = project.status === 'chatting' && !seenIds.includes(project.id);
                                const needsAction = ACTION_NEEDED.has(project.status);
                                const customerName = project.customer
                                    ? `${project.customer.last_name} ${project.customer.first_name} 様`
                                    : `顧客 #${project.customer_id}`;
                                const workType = project.condition?.work_types?.join('・') || '';
                                const locationCity = project.condition?.location_city || '';

                                return (
                                    <div
                                        key={project.id}
                                        style={{
                                            background: project.status === 'decided' ? '#fffbeb' : '#fff',
                                            borderRadius: 8,
                                            padding: '18px 20px',
                                            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                            borderLeft: `3px solid ${project.status === 'decided' ? '#f59e0b' : needsAction ? '#005c53' : '#e0e4ea'}`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            flexWrap: 'wrap',
                                            gap: 14,
                                        }}
                                    >
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: (workType || locationCity) ? 6 : 0 }}>
                                                <span style={{ fontWeight: 600, fontSize: '0.97rem', color: '#1a1a2e' }}>
                                                    {customerName}
                                                </span>
                                                {isNew && (
                                                    <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700, background: '#fee2e2', color: '#dc2626' }}>
                                                        新規
                                                    </span>
                                                )}
                                                <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: '0.76rem', fontWeight: 500, background: meta.bg, color: meta.color }}>
                                                    {meta.label}
                                                </span>
                                            </div>
                                            {(workType || locationCity) && (
                                                <p style={{ fontSize: '0.83rem', color: '#6b7280', margin: 0 }}>
                                                    {workType}{locationCity ? `　${locationCity}` : ''}
                                                </p>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                            <button
                                                onClick={() => navigate(`/contractor/chat/${project.id}`)}
                                                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', background: '#fff', color: '#6b7280', border: '1px solid #e0e4ea', borderRadius: 4, fontSize: '0.8rem', cursor: 'pointer' }}
                                            >
                                                <MessageIcon /> チャット
                                            </button>
                                            <button
                                                onClick={() => handleDetail(project.id)}
                                                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', background: '#005c53', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.83rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                                            >
                                                詳細 <ArrowRight />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* 過去の案件 */}
                {doneProjects.length > 0 && (
                    <div>
                        <h2 style={{ fontSize: '1rem', fontWeight: 600, color: '#9ca3af', marginBottom: 12 }}>過去の案件</h2>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {doneProjects.map(project => {
                                const meta = STATUS_META[project.status] || { label: project.status, bg: '#e0e4ea', color: '#6b7280' };
                                const customerName = project.customer
                                    ? `${project.customer.last_name} ${project.customer.first_name} 様`
                                    : `顧客 #${project.customer_id}`;
                                const workType = project.condition?.work_types?.join('・') || '';
                                return (
                                    <div
                                        key={project.id}
                                        style={{
                                            background: '#fff', borderRadius: 8,
                                            padding: '14px 18px',
                                            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            opacity: 0.75,
                                        }}
                                    >
                                        <div>
                                            <span style={{ fontWeight: 500, color: '#1a1a2e', fontSize: '0.9rem' }}>
                                                {customerName}
                                            </span>
                                            {workType && (
                                                <span style={{ marginLeft: 8, fontSize: '0.8rem', color: '#9ca3af' }}>
                                                    {workType}
                                                </span>
                                            )}
                                        </div>
                                        <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: '0.76rem', fontWeight: 500, background: meta.bg, color: meta.color }}>
                                            {meta.label}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default ContractorDashboard;
